package com.dotdrives.web.pages.drives

import com.dotdrives.model.DriveFinancials
import com.dotdrives.model.DrivePhase
import com.dotdrives.model.DriveSummary
import kotlin.math.round
import kotlin.time.Instant

fun driveInitials(displayName: String): String =
    displayName.split(' ', '&')
        .filter(String::isNotBlank)
        .take(2)
        .joinToString("") { it.first().uppercase() }
        .ifBlank { "?" }

fun driveSubtitle(giverType: String, stewardName: String?): String =
    "${giverType.replaceFirstChar(Char::uppercase)} · Steward: ${stewardName ?: "Unassigned"}"

/** Abbreviated money for board cards and column/header totals — "$8K", "$1.2M". Null renders "—". */
fun formatMoneyCompact(amount: Double?): String {
    if (amount == null) return MONEY_HIDDEN
    val abs = kotlin.math.abs(amount)
    val sign = if (amount < 0) "-" else ""
    return when {
        abs >= 1_000_000 -> "$sign$${trimDecimal(abs / 1_000_000)}M"
        abs >= 1_000 -> "$sign$${trimDecimal(abs / 1_000)}K"
        else -> "$sign$${abs.toLong()}"
    }
}

/** Full, comma-grouped money for the detail panel — "$25,000". Null renders "—". */
fun formatMoneyFull(amount: Double?): String {
    if (amount == null) return MONEY_HIDDEN
    val whole = round(amount).toLong()
    val sign = if (whole < 0) "-" else ""
    return "$sign$${groupThousands(kotlin.math.abs(whole))}"
}

private fun trimDecimal(value: Double): String {
    val rounded = round(value * 10) / 10
    val asLong = rounded.toLong()
    return if (rounded == asLong.toDouble()) asLong.toString() else rounded.toString()
}

private fun groupThousands(value: Long): String =
    value.toString().reversed().chunked(3).joinToString(",").reversed()

private const val MONEY_HIDDEN = "—"

data class DriveAge(
    val label: String,
    val toneClass: String,
)

/** Time-in-phase label + a CSS tone bucket, matching the design's age color escalation. */
fun driveAge(phaseEnteredAt: Instant, now: Instant): DriveAge {
    val days = (now - phaseEnteredAt).inWholeDays
    return when {
        days < 1 -> DriveAge("TODAY", "drive-age-today")
        days <= 6 -> DriveAge("${days}d", "drive-age-normal")
        days <= 13 -> DriveAge("${days}d", "drive-age-stale")
        else -> DriveAge("${days}d", "drive-age-critical")
    }
}

fun taskProgressPercent(complete: Int, total: Int): Int =
    if (total <= 0) 0 else ((complete * 100) / total).coerceIn(0, 100)

data class PhaseColumn(
    val phase: DrivePhase,
    val drives: List<DriveSummary>,
    val totalAskAmount: Double,
)

fun groupDrivesByPhase(phases: List<DrivePhase>, drives: List<DriveSummary>): List<PhaseColumn> =
    phases.sortedBy(DrivePhase::sequence).map { phase ->
        val phaseDrives = drives.filter { it.currentPhaseId == phase.id }
        PhaseColumn(
            phase = phase,
            drives = phaseDrives,
            totalAskAmount = phaseDrives.sumOf { it.askAmount ?: 0.0 },
        )
    }

data class FinancialsTotals(
    val proposed: Double?,
    val pledged: Double?,
    val received: Double?,
)

/** Sums the visible drive_financials rows. Empty because RLS hid every row (field role) renders as null → "—". */
fun summarizeFinancials(financials: Map<String, DriveFinancials>, driveIds: List<String>): FinancialsTotals {
    if (driveIds.isEmpty()) return FinancialsTotals(0.0, 0.0, 0.0)
    val matched = driveIds.mapNotNull(financials::get)
    if (matched.isEmpty()) return FinancialsTotals(null, null, null)
    return FinancialsTotals(
        proposed = matched.sumOf(DriveFinancials::proposedAmount),
        pledged = matched.sumOf(DriveFinancials::pledgedAmount),
        received = matched.sumOf(DriveFinancials::receivedAmount),
    )
}

/**
 * The completed list must not present `ask_amount` (what was asked) as money that actually
 * moved — only `drive_financials` knows that. Returns the amount that matches the drive's
 * outcome (received → receivedAmount, pledged → pledgedAmount), or null both for a
 * `declined` drive (nothing was ever raised) and for a drive whose financials row is hidden
 * by RLS — both render "—" via `formatMoneyFull`.
 */
fun completedRowAmount(drive: DriveSummary, financials: Map<String, DriveFinancials>): Double? =
    when (drive.outcome) {
        "received" -> financials[drive.id]?.receivedAmount
        "pledged" -> financials[drive.id]?.pledgedAmount
        else -> null
    }

/**
 * Sums `receivedAmount` across the received-outcome drives in this page. Null when every
 * matching drive's financials row is hidden by RLS (field role) — renders "—", same
 * degradation as [summarizeFinancials].
 */
fun completedRaisedTotal(drives: List<DriveSummary>, financials: Map<String, DriveFinancials>): Double? {
    val receivedIds = drives.filter { it.outcome == "received" }.map(DriveSummary::id)
    if (receivedIds.isEmpty()) return 0.0
    val matched = receivedIds.mapNotNull(financials::get)
    if (matched.isEmpty()) return null
    return matched.sumOf(DriveFinancials::receivedAmount)
}

fun paginationWindow(currentPage: Int, totalPages: Int): List<Int?> {
    if (totalPages <= 1) return listOf(1)
    if (totalPages <= 5) return (1..totalPages).toList()

    val pages = buildSet {
        add(1)
        add(totalPages)
        if (currentPage <= 3) {
            addAll(2..4)
        } else if (currentPage >= totalPages - 2) {
            addAll((totalPages - 3)..(totalPages - 1))
        } else {
            addAll((currentPage - 1)..(currentPage + 1))
        }
    }.sorted()

    return buildList {
        pages.forEachIndexed { index, page ->
            if (index > 0 && page - pages[index - 1] > 1) add(null)
            add(page)
        }
    }
}

/** Closing behavior is driven by the persisted configuration, independent of phase names. */
fun isTerminalPhase(phase: com.dotdrives.model.DrivePhase): Boolean = phase.isTerminal
